import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  format, startOfWeek, endOfWeek, getWeek, parseISO,
} from 'date-fns';
import { Loader2, Printer, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Team weekly timesheet PDF preview.
 *
 * Replaces the broken legacy handlePrintTeamView() in Timesheet.js which
 * used `window.open('', '_blank') + document.write()` — that pattern
 * blanks the print preview in Safari (DOM gets detached the moment
 * `window.print()` fires) and is increasingly unreliable in Chrome/Edge
 * too because of cross-origin / about:blank security tightening.
 *
 * Same proven pattern as TimesheetMemberPreview / PreConPreview / etc.:
 *  - Render the printable HTML in the SAME tab as a real React route
 *  - Hide chrome via `.no-print { display:none }` under `@media print`
 *  - Call `window.print()` from a button on that page
 */
const hoursToMinutes = (h) => (h == null ? null : Math.round(Number(h) * 60));
const formatMinutesToDisplay = (mins) => {
  const m = Math.max(0, Math.round(Number(mins) || 0));
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h && r) return `${h}h ${r}m`;
  if (h) return `${h}h`;
  return `${r}m`;
};

export default function TimesheetTeamPreview() {
  const { weekStart } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [loading, setLoading] = useState(true);
  const [teamSummary, setTeamSummary] = useState([]);

  const start = startOfWeek(parseISO(weekStart), { weekStartsOn: 0 });
  const end = endOfWeek(start, { weekStartsOn: 0 });

  // Inject print styles into <head> — Safari's Save-as-PDF path snapshots
  // stylesheets at the moment print() fires and can miss <style> tags
  // rendered as JSX children, which is what was producing blank PDFs.
  useEffect(() => {
    const el = document.createElement('style');
    el.setAttribute('data-tts-print-styles', '1');
    el.textContent = TS_TEAM_PREVIEW_STYLES;
    document.head.appendChild(el);
    return () => { el.remove(); };
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/timesheets/team/summary`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            start_date: format(start, 'yyyy-MM-dd'),
            end_date: format(end, 'yyyy-MM-dd'),
          },
        });
        setTeamSummary(res.data || []);
      } catch (e) {
        console.error('Failed to load team summary', e);
        toast.error('Failed to load team timesheet summary');
      } finally {
        setLoading(false);
      }
    };
    if (weekStart && token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart, token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="flex items-center gap-3 text-slate-600">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span>Loading team summary...</span>
        </div>
      </div>
    );
  }

  const teamTotalTravelMin = teamSummary.reduce(
    (s, m) => s + (m.travel_minutes ?? hoursToMinutes(m.travel_hours) ?? 0), 0,
  );
  const teamTotalOnJobMin = teamSummary.reduce(
    (s, m) => s + (m.on_job_minutes ?? hoursToMinutes(m.on_job_hours) ?? 0), 0,
  );
  const teamTotalMin = teamSummary.reduce(
    (s, m) => s + (m.total_minutes ?? hoursToMinutes(m.total_hours) ?? 0), 0,
  );
  const teamTotalEntries = teamSummary.reduce((s, m) => s + (m.entry_count || 0), 0);

  return (
    <div className="tts-preview-root">
      {/* Sticky toolbar — hidden in print via .no-print */}
      <div className="no-print sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200 transition-all mr-2"
              data-testid="tts-preview-back"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <span className="font-semibold text-slate-700 text-sm">
              Team Timesheet — Week {getWeek(start)} · {format(start, 'yyyy')}
            </span>
          </div>
          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-orange-600 text-white hover:bg-orange-700 transition-all shadow-sm"
            data-testid="tts-preview-print"
          >
            <Printer className="w-4 h-4" />
            Print / Save PDF
          </button>
        </div>
      </div>

      {/* Printable document */}
      <div className="tts-doc">
        {/* Brand strip */}
        <div className="brand">
          <img
            src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/69b5afabd540121a86879b5b/e29b4c6b2_CLLLogoTransparent.png"
            alt="Center Line Locating"
          />
          <div className="brand-info">
            <b>Center Line Locating</b><br />
            12030 Goodrich Dr, Charlotte, NC 28273 &nbsp;·&nbsp; (704) 315-5820 &nbsp;·&nbsp; requests@cllocating.com
          </div>
          <div className="brand-doc">
            <div className="lbl">Team Summary</div>
            <div className="num">#TTS-{getWeek(start)}-{format(start, 'yyyy')}</div>
          </div>
        </div>

        {/* Info row */}
        <div className="info-section">
          <div className="info-grid">
            <div className="info-item">
              <label>Report Type</label>
              <span>Team Summary</span>
            </div>
            <div className="info-item">
              <label>Period</label>
              <span>{format(start, 'MMMM d')} – {format(end, 'MMMM d, yyyy')}</span>
            </div>
            <div className="info-item">
              <label>Team Total Hours</label>
              <span className="total-badge">{formatMinutesToDisplay(teamTotalMin)}</span>
            </div>
          </div>
        </div>

        {/* Team Table */}
        {teamSummary.length === 0 ? (
          <div className="empty">No team data for this week.</div>
        ) : (
          <table className="tts-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Email</th>
                <th style={{ textAlign: 'center' }}>Role</th>
                <th style={{ textAlign: 'right' }}>Travel</th>
                <th style={{ textAlign: 'right' }}>On Job</th>
                <th style={{ textAlign: 'right' }}>Total</th>
                <th style={{ textAlign: 'center' }}>Entries</th>
              </tr>
            </thead>
            <tbody>
              {teamSummary.map((m, i) => (
                <tr key={m.user_id || m.user_email || i}>
                  <td>{m.user_name || '—'}</td>
                  <td>{m.user_email || '—'}</td>
                  <td style={{ textAlign: 'center' }}>{m.role || '—'}</td>
                  <td style={{ textAlign: 'right' }}>
                    {formatMinutesToDisplay(m.travel_minutes ?? hoursToMinutes(m.travel_hours))}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {formatMinutesToDisplay(m.on_job_minutes ?? hoursToMinutes(m.on_job_hours))}
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 600, color: '#f59e0b' }}>
                    {formatMinutesToDisplay(m.total_minutes ?? hoursToMinutes(m.total_hours))}
                  </td>
                  <td style={{ textAlign: 'center' }}>{m.entry_count ?? 0}</td>
                </tr>
              ))}
              <tr className="total-row">
                <td colSpan={3} style={{ textAlign: 'right', fontWeight: 600 }}>TEAM TOTAL:</td>
                <td style={{ textAlign: 'right' }}>{formatMinutesToDisplay(teamTotalTravelMin)}</td>
                <td style={{ textAlign: 'right' }}>{formatMinutesToDisplay(teamTotalOnJobMin)}</td>
                <td style={{ textAlign: 'right', fontWeight: 700, color: '#b45309', fontSize: 14 }}>
                  {formatMinutesToDisplay(teamTotalMin)}
                </td>
                <td style={{ textAlign: 'center' }}>{teamTotalEntries}</td>
              </tr>
            </tbody>
          </table>
        )}

        <div className="footer">
          Generated from CenterLine Locating FieldReady on {format(new Date(), 'MMMM d, yyyy h:mm a')}
        </div>
      </div>
    </div>
  );
}

// CSS for TimesheetTeamPreview, injected into <head> via useEffect to
// dodge Safari's "Save as PDF" blank-PDF bug when print rules live in a
// JSX-rendered <style> child. Same approach as TimesheetMemberPreview.
const TS_TEAM_PREVIEW_STYLES = `
        .tts-preview-root { min-height: 100vh; background:#f1f5f9; }
        .tts-doc { font-family:'Segoe UI', Arial, sans-serif; padding:40px; max-width:900px; margin:18px auto; background:#fff; color:#1e293b; border-radius:8px; box-shadow:0 2px 10px rgba(0,0,0,0.04); }
        .brand { display:flex; justify-content:space-between; align-items:flex-start; padding-bottom:15px; border-bottom:1px solid #e2e8f0; margin-bottom:20px; }
        .brand img { height:80px; width:auto; object-fit:contain; }
        .brand-info { flex:1; text-align:center; padding:0 20px; font-size:12px; color:#64748b; line-height:1.5; }
        .brand-info b { font-weight:500; color:#334155; }
        .brand-doc { text-align:right; flex-shrink:0; }
        .brand-doc .lbl { font-size:10px; font-weight:600; color:#94a3b8; text-transform:uppercase; letter-spacing:1px; }
        .brand-doc .num { font-size:16px; font-weight:700; color:#1e293b; margin-top:2px; }
        .info-section { background:#f8fafc; border-radius:8px; padding:20px; margin-bottom:25px; }
        .info-grid { display:grid; grid-template-columns:repeat(3, 1fr); gap:20px; }
        .info-item label { font-size:10px; font-weight:600; color:#64748b; text-transform:uppercase; letter-spacing:0.5px; display:block; margin-bottom:4px; }
        .info-item span { font-size:14px; color:#1e293b; font-weight:500; }
        .total-badge { display:inline-block; background:#fef3c7; color:#b45309; padding:6px 16px; border-radius:20px; font-weight:700; font-size:16px; }
        .tts-table { width:100%; border-collapse:collapse; margin-bottom:25px; font-size:12px; }
        .tts-table th { background:#f1f5f9; color:#475569; padding:12px 10px; text-align:left; font-weight:600; font-size:11px; text-transform:uppercase; letter-spacing:0.5px; border-bottom:2px solid #e2e8f0; }
        .tts-table td { padding:10px; border-bottom:1px solid #e2e8f0; }
        .tts-table .total-row { background:#f8fafc; font-weight:600; }
        .tts-table .total-row td { border-bottom:none; border-top:2px solid #e2e8f0; }
        .empty { padding:40px; text-align:center; color:#94a3b8; font-style:italic; border:1px dashed #e2e8f0; border-radius:8px; }
        .footer { text-align:center; color:#94a3b8; font-size:11px; margin-top:40px; padding-top:20px; border-top:1px solid #e2e8f0; }

        /* Print rules — same proven pattern, registered in <head> for Safari */
        @media print {
          .no-print { display:none !important; }
          html, body { background:#fff !important; }
          .tts-preview-root { background:#fff !important; }
          .tts-doc { box-shadow:none !important; max-width:none !important; margin:0 !important; padding:20px !important; border-radius:0 !important; }
          .tts-doc, .tts-doc * { -webkit-print-color-adjust:exact !important; print-color-adjust:exact !important; color-adjust:exact !important; }
        }
        @page { size: letter portrait; margin: 0.5in; }
`;
